import React, { useMemo } from 'react';
import cn from 'clsx';

import Select from '../../../../library/select/select';
import FitMessage from '../../../../components/fit-message/fit-message';
import FilterButton from '../filter-button/filter-button';

import { Fitment } from '../../../../shared/types/fitment';
import {
  DoesFitEnum,
  ProductTypeEnum
} from '../../../../shared/types/graphql-types';
import { FIT } from '../../../../shared/constants/fit';
import {
  DEFAULT_PAGE_SIZE,
  PRODUCT_TYPES
} from '../../../../shared/constants/products';

import styles from './results-bar.scss';

const defaultPageSizeOptions = [
  { label: DEFAULT_PAGE_SIZE, selected: false, value: DEFAULT_PAGE_SIZE },
  { label: 24, selected: false, value: 24 },
  { label: 36, selected: false, value: 36 }
];

interface ResultsBarProps {
  currentSort: string;
  doesFitEnum: DoesFitEnum;
  filterCount: number;
  hidePageSize?: boolean;
  infoMessage?: { id: number };
  isLoading: boolean;
  isPackage: boolean;
  onPageSizeChange: (data) => void;
  onSelectSort: (data) => void;
  onToggleMobileFilter: () => void;
  pageSize: number;
  productType: ProductTypeEnum;
  resultsText: string;
  selectedFitment: Fitment;
  showCount: boolean;
  showFilter: boolean;
  showFitmentMsg?: boolean;
  sorts: string[];
  totalNumberOfResults: string | number;
}

const ResultsBar = React.forwardRef<HTMLDivElement, ResultsBarProps>(
  function ResultsBar(props, ref) {
    const {
      currentSort,
      doesFitEnum,
      filterCount,
      hidePageSize = false,
      infoMessage,
      isLoading = false,
      isPackage = false,
      onPageSizeChange,
      onSelectSort,
      onToggleMobileFilter,
      pageSize,
      productType,
      resultsText,
      selectedFitment,
      showCount = false,
      showFilter = false,
      showFitmentMsg = false,
      sorts,
      totalNumberOfResults
    } = props;

    let sortOptions;
    if (sorts && typeof sorts === 'object' && Object.keys(sorts).length > 0) {
      sortOptions = Object.keys(sorts).map(sort => sorts[sort]);
    }

    const pageSizeOptions = useMemo(
      () =>
        defaultPageSizeOptions.map(size =>
          size.value === pageSize
            ? { ...size, selected: true }
            : { ...size, selected: false }
        ),
      [pageSize]
    );
    const selectedPageSize = pageSizeOptions.find(
      size => size.selected === true
    );

    return (
      <div
        className={cn(
          `${
            !showFitmentMsg
              ? styles['container']
              : styles['container-without-fit-msg']
          } ${
            productType === PRODUCT_TYPES.TIRES
              ? styles['container--no-border']
              : ''
          }`,
          'results-bar'
        )}
        ref={ref}
      >
        {showFilter && (
          <FilterButton
            filterCount={filterCount}
            onToggleMobileFilter={onToggleMobileFilter}
          />
        )}

        {showCount && (
          <div className={cn(styles['count-line'], 'results-bar__count-line')}>
            <strong>{totalNumberOfResults || 0}</strong> {resultsText || ''}
          </div>
        )}

        {!showFitmentMsg && (
          <FitMessage
            data-testid="FitMessage"
            doesFit={doesFitEnum === FIT}
            fitment={selectedFitment}
            infoMessage={infoMessage}
            isLoading={isLoading}
            isPackage={isPackage}
            productType={productType}
          />
        )}

        {!hidePageSize && (
          <div>
            <Select
              label="Per Page"
              onChange={data => onPageSizeChange(data.value)}
              options={pageSizeOptions}
              value={selectedPageSize}
            />
          </div>
        )}

        {currentSort && sorts?.[currentSort] && (
          <div
            className={cn(
              styles['select-wrapper'],
              'results-bar__select-wrapper'
            )}
          >
            <Select
              data-testid="plp-results-sort"
              label="Sort"
              onChange={data => onSelectSort(data)}
              options={sortOptions}
              value={sorts[currentSort]}
            />
          </div>
        )}
      </div>
    );
  }
);
export default ResultsBar;
